import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * SuggestionDialog - Modal dialog for handling rejected analytics questions
 *
 * Displays when guard agent rejects a question, showing:
 * - Rejection reason
 * - Suggested analytics-related alternative
 * - Three action options: Accept, Revise, or Cancel
 *
 * @param {boolean} open - Dialog visibility
 * @param {object} validation - Validation error details
 * @param {string} validation.reason - Rejection reason
 * @param {string} validation.suggested_question - Alternative question suggestion
 * @param {function} onAccept - Callback when user accepts suggestion (auto-submits)
 * @param {function} onRevise - Callback when user wants to revise suggestion
 * @param {function} onCancel - Callback when user cancels
 */
const SuggestionDialog = ({
  open,
  validation,
  onAccept,
  onRevise,
  onCancel
}) => {
  const reason = validation?.reason || 'This question could not be validated.';
  const suggestedQuestion = validation?.suggested_question || null;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="suggestion-dialog-title"
      aria-describedby="suggestion-dialog-description"
    >
      <DialogTitle id="suggestion-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon color="primary" />
          <Typography variant="h6">Question Suggestion</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {reason}
          </Alert>

          {suggestedQuestion && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Try asking:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  borderLeft: 3,
                  borderColor: 'primary.main',
                  fontStyle: 'italic'
                }}
              >
                "{suggestedQuestion}"
              </Typography>
            </Box>
          )}

          {!suggestedQuestion && (
            <Typography variant="body2" color="text.secondary">
              Please try rephrasing your question to focus on AI capabilities, training needs, team performance, or organizational assessment data.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>

        {suggestedQuestion && (
          <>
            <Button onClick={onRevise} color="primary">
              Revise
            </Button>
            <Button
              onClick={onAccept}
              variant="contained"
              color="primary"
              autoFocus
            >
              Accept Suggested
            </Button>
          </>
        )}

        {!suggestedQuestion && (
          <Button
            onClick={onCancel}
            variant="contained"
            color="primary"
            autoFocus
          >
            OK
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SuggestionDialog;
